import { FormEvent, useEffect, useState } from 'react';

import {
  listNotifications,
  NotificationRecord,
  sendNotification,
  SendNotificationResponse,
} from '../api/notifications';
import { ApiVersionSummary, listVersions } from '../api/releases';

const DEFAULT_API_ID = 'skymap';

/** Default subject line generated from the selected release. */
const releaseSubject = (versionId: string) => `New SkyMap API release available: ${versionId}`;

export default function AdminNotificationsPage() {
  const [notifications, setNotifications] = useState<NotificationRecord[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [listError, setListError] = useState<string | null>(null);

  const [subject, setSubject] = useState('');
  const [htmlBody, setHtmlBody] = useState('');
  const [versionId, setVersionId] = useState('');
  const [versions, setVersions] = useState<ApiVersionSummary[]>([]);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState<{
    ok: boolean;
    data?: SendNotificationResponse;
    error?: string;
  } | null>(null);

  const loadNotifications = () => {
    setLoadingList(true);
    setListError(null);
    listNotifications()
      .then((res) => setNotifications(res.items))
      .catch(() => setListError('Failed to load notification history.'))
      .finally(() => setLoadingList(false));
  };

  useEffect(() => {
    loadNotifications();
  }, []);

  // Load released versions for the dropdown. Non-fatal if it fails — the admin
  // can still type a subject manually.
  useEffect(() => {
    listVersions(DEFAULT_API_ID)
      .then((all) =>
        setVersions(
          all
            .filter((v) => v.releaseState === 'released')
            .sort((a, b) => b.versionId.localeCompare(a.versionId)),
        ),
      )
      .catch(() => setVersions([]));
  }, []);

  // Selecting a release auto-fills the subject (still editable afterwards).
  const handleVersionChange = (selected: string) => {
    setVersionId(selected);
    setSubject(selected ? releaseSubject(selected) : '');
  };

  const handleSend = async (e: FormEvent) => {
    e.preventDefault();
    if (!subject.trim() || !htmlBody.trim()) {
      return;
    }

    const confirmed = window.confirm(
      `Send "${subject}" to all active subscribers?\n\nThis cannot be undone.`,
    );
    if (!confirmed) {
      return;
    }

    setSending(true);
    setSendResult(null);
    try {
      const data = await sendNotification({
        subject: subject.trim(),
        htmlBody: htmlBody.trim(),
        versionId: versionId.trim() || undefined,
      });
      setSendResult({ ok: true, data });
      setSubject('');
      setHtmlBody('');
      setVersionId('');
      loadNotifications();
    } catch (err) {
      setSendResult({ ok: false, error: (err as Error).message ?? 'Send failed.' });
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{ maxWidth: 800, margin: '0 auto', padding: '32px 16px' }}>
      <h2>Send notification</h2>

      <form style={{ marginBottom: 48 }} onSubmit={(e) => void handleSend(e)}>
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', fontWeight: 500, marginBottom: 4, fontSize: 14 }}>
            Release
          </label>
          <select
            style={{
              width: '100%',
              padding: '8px 10px',
              borderRadius: 6,
              border: '1px solid var(--border-strong)',
              fontSize: 14,
              boxSizing: 'border-box',
              background: 'var(--bg-surface-2)',
            }}
            value={versionId}
            onChange={(e) => handleVersionChange(e.target.value)}
          >
            <option value="">Select a released version…</option>
            {versions.map((v) => (
              <option key={v.versionId} value={v.versionId}>
                {v.displayName || v.versionId}
              </option>
            ))}
          </select>
          {versions.length === 0 && (
            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
              No released versions yet — you can still enter a subject manually below.
            </p>
          )}
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', fontWeight: 500, marginBottom: 4, fontSize: 14 }}>
            Subject
          </label>
          <input
            placeholder="Select a release above, or type a subject"
            required={true}
            style={{
              width: '100%',
              padding: '8px 10px',
              borderRadius: 6,
              border: '1px solid var(--border-strong)',
              fontSize: 14,
              boxSizing: 'border-box',
            }}
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />
          <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
            Auto-filled from the selected release — edit if needed.
          </p>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontWeight: 500, marginBottom: 4, fontSize: 14 }}>
            Email body (HTML)
          </label>
          <textarea
            placeholder="<p>We have released a new version of the SkyMap API…</p>"
            required={true}
            rows={10}
            style={{
              width: '100%',
              padding: '8px 10px',
              borderRadius: 6,
              border: '1px solid var(--border-strong)',
              fontSize: 13,
              fontFamily: 'monospace',
              boxSizing: 'border-box',
              resize: 'vertical',
            }}
            value={htmlBody}
            onChange={(e) => setHtmlBody(e.target.value)}
          />
          <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
            An unsubscribe link and footer are added automatically.
          </p>
        </div>

        {sendResult && (
          <div
            style={{
              padding: 12,
              borderRadius: 6,
              background: sendResult.ok ? 'var(--success-bg)' : 'var(--danger-bg)',
              border: `1px solid ${sendResult.ok ? 'var(--success-border)' : 'var(--danger-border)'}`,
              color: sendResult.ok ? 'var(--success-text)' : 'var(--danger-text)',
              marginBottom: 12,
              fontSize: 14,
            }}
          >
            {sendResult.ok
              ? `Sent to ${sendResult.data?.recipientCount ?? 0} subscriber(s)${sendResult.data?.failedCount ? ` (${sendResult.data.failedCount} failed)` : ''}.`
              : sendResult.error}
          </div>
        )}

        <button
          disabled={sending || !subject.trim() || !htmlBody.trim()}
          style={{
            padding: '10px 24px',
            background: 'var(--accent-solid)',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            fontSize: 14,
            fontWeight: 600,
            cursor: sending ? 'not-allowed' : 'pointer',
            opacity: sending || !subject.trim() || !htmlBody.trim() ? 0.6 : 1,
          }}
          type="submit"
        >
          {sending ? 'Sending…' : 'Send to all active subscribers'}
        </button>
      </form>

      <h2>Notification history</h2>
      {loadingList && <p style={{ color: 'var(--text-muted)' }}>Loading…</p>}
      {listError && <p style={{ color: 'var(--danger-text)' }}>{listError}</p>}

      {!loadingList && !listError && notifications.length === 0 && (
        <p style={{ color: 'var(--text-muted)' }}>No notifications sent yet.</p>
      )}

      {notifications.map((n) => (
        <div
          key={n.id}
          style={{
            padding: '12px 16px',
            border: '1px solid var(--border-subtle)',
            borderRadius: 8,
            marginBottom: 10,
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 4 }}>{n.subject}</div>
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            {new Date(n.sentAt).toLocaleString()} · {n.recipientCount} sent
            {n.failedCount > 0 && (
              <span style={{ color: 'var(--danger-text)', marginLeft: 8 }}>
                ({n.failedCount} failed)
              </span>
            )}
            {n.versionId && <span style={{ marginLeft: 8 }}>· version {n.versionId}</span>}
          </div>
        </div>
      ))}
    </div>
  );
}
